<script setup lang="ts">
import BaseSingleInput from "@/core/components/base/BasePhoneInput.vue";
import CustomerSearch from "@/customer/components/customer-search/CustomerSearch.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Dialog, DialogContent, DialogTrigger, DialogTitle} from "@/core/components/ui/dialog";
import Icon from "@/core/components/icon/Icon.vue";
import {ref} from "vue";
import Customer from "@/customer/interfaces/Customer";
import formatHelper from "@/core/helpers/formatHelper";
import {customerCertificationApi} from "@/container";
import CustomerCertificationNotificationModal
    from "@/customer-certification/views/CustomerCertificationNotificationModal.vue";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/core/components/ui/tabs";
import {useRouter} from "vue-router";
import {Skeleton} from "@/core/components/ui/skeleton";

defineProps<{ customers: Customer[]; loading: boolean }>();

const healthcenterCustomer = ref<Customer>();
const phone = ref<string>('');
const phoneLoading = ref<boolean>(false);
const mainModalOpen = ref(false);
const modalVisible = ref<boolean>(false);
const error = ref<string | null>(null);
const router = useRouter();


/**
 * Certify a phone number for the current customer.
 *
 * @param customer
 */
const certifyPhone = (customer: any) => {
    if (!formatHelper.phoneIsValid(phone.value)) {
        error.value = 'Le numéro doit correspondre au format 06 XX XX XX XX ou 07 XX XX XX XX';
        return;
    } else {
        error.value = null;
    }

    phoneLoading.value = true;

    customerCertificationApi()
        .certifyPhone(customer.id, formatHelper.apiPhone(phone.value))
        .then((certification) => {
            if (healthcenterCustomer.value) {
                healthcenterCustomer.value.certification = certification;
            }
            mainModalOpen.value = false;
            modalVisible.value = true;
        })
        .catch((error) => {
            error.value = 'Le patient n\'a pas pu être certifié, vérifiez que ce dernier n\'est pas déjà certifié';
            console.log(error);
        })
        .finally(() => {
            phoneLoading.value = false;
        });
};
</script>

<template>
  <customer-certification-notification-modal
    v-if="healthcenterCustomer"
    class="z-30"
    :modal-visible="modalVisible"
    :customer="healthcenterCustomer"
    @close="modalVisible = false"
  />
  <Dialog v-model:open="mainModalOpen">
    <DialogTrigger>
      <slot />
    </DialogTrigger>
    <DialogContent>
      <DialogTitle>
        <h2>Essayer l'app !</h2>
      </DialogTitle>
      <p>Renseignez votre numéro de mobile</p>
      <BaseSingleInput
        action=""
        placeholder="06 __ __ __ __"
        format="phone"
        :input="phone"
        :loading="phoneLoading"
        @on-change="value => phone = value"
      />
      <p>
        <span
          v-if="error"
          class="text-sm text-red-500"
        >
          {{ error }}
        </span>
      </p>
      <p>Recherchez-vous dans la liste de vos patients</p>
      <Tabs class="flex flex-col w-full" :default-value="customers.length > 0 ? 'list' : 'search'">
        <TabsList class="relative shrink-0 flex bg-gray-100 border-primary border rounded-lg">
          <TabsTrigger
            class="flex flex-1 justify-center p-2 px-4 rounded-lg hover:opacity-75 hover:cursor-pointer data-[state=active]:bg-white"
            value="list"
          >
            <h2 class="text-lg">
              Liste
            </h2>
          </TabsTrigger>
          <TabsTrigger
            class="flex flex-1 justify-center p-2 px-4 rounded-lg hover:opacity-75 hover:cursor-pointer data-[state=active]:bg-white"
            value="search"
          >
            <h2 class="text-lg">
              Recherche
            </h2>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="list" class="py-4">
          <div v-if="loading">
            <Skeleton class="w-full h-4" />
          </div>
          <div v-else-if="customers.length > 0">
            <div
              v-for="customer in customers"
              :key="customer.id"
              class="flex flex-col gap-2"
            >
              <div
                class="flex flex-row items-center p-2 hover:opacity-90 hover:bg-gray-100 cursor-pointer rounded-lg"
                @click="() => healthcenterCustomer=customer"
              >
                <Icon
                  :size="16"
                  class="mr-2"
                  :name="customer.information.gender == 'male' ? 'mdi-face-man': 'mdi-face-woman' "
                />
                <p>
                  {{ customer.information.name }} ({{
                    $filters.date(customer.information.birth_date, "relative", true)
                  }})
                </p>
                <div class="flex flex-1 flex-row justify-end">
                  <icon
                    v-if="customer.status === 'pre-certified' || customer.status === 'validated'"
                    :size="16"
                    class="m-2"
                    name="mdi-cellphone"
                  />
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-sm">
            Aucun patient ne correspond à votre prénom/nom, utilisez l'onglet Recherche pour sélectionner un
            patient
          </p>
        </TabsContent>
        <TabsContent value="search" class="py-4">
          <CustomerSearch
            class="w-full bg-gray-50"
            :callback="(type, id, key, customer) => healthcenterCustomer = customer"
            placeholder="Nom du patient ..."
          />
        </TabsContent>
      </Tabs>
      <div
        v-if="!!healthcenterCustomer"
      >
        <p>Vous avez sélectionné le patient suivant :</p>
        <div
          class="flex flex-row items-center p-2 hover:opacity-90 hover:bg-gray-100 cursor-pointer border-2 border-primary rounded-lg"
        >
          <Icon
            :size="16"
            class="mr-2"
            :name="healthcenterCustomer.information.gender == 'male' ? 'mdi-face-man': 'mdi-face-woman' "
          />
          <p>
            {{ healthcenterCustomer.information.name }} ({{
              $filters.date(healthcenterCustomer.information.birth_date, "relative", true)
            }})
          </p>
          <div class="flex flex-1 flex-row justify-end">
            <icon
              v-if="healthcenterCustomer.status !== 'none'"
              :size="16"
              class="m-2"
              name="mdi-cellphone"
            />
          </div>
        </div>
        <p v-if="healthcenterCustomer.status === 'validated'" class="text-red px-4 pt-2">
          Patient déjà certifié. <span class="underline cursor-pointer" @click="router.push({name: 'customer.home', params: { customerId: healthcenterCustomer.id}})">Voir la fiche patient</span>
        </p>
      </div>
      <div class="flex justify-center gap-2">
        <base-button
          primary
          class="mt-2"
          :disabled="!healthcenterCustomer || healthcenterCustomer.status === 'validated'"
          @click="() => certifyPhone(healthcenterCustomer)"
        >
          Certifier
        </base-button>
      </div>
    </DialogContent>
  </Dialog>
</template>
